import { BANK_ACCOUNTS_PATH, EXPENSES_PATH, PLAID_FEED_KEY, SCRIPTED_FEED_KEY, STRIPE_FEED_KEY, t } from '@bms2/shared';
/**
 * The banking side's routes (piece 24). 🔨 Relative to the API's own root,
 * never prefixed with /api: the base URL carries the prefix, and a path that
 * repeats it 404s silently because the shell swallows a failed read.
 */
// The list route is declared once in the package; the Banking pages that read it from here keep this one export (T3214).
export { BANK_ACCOUNTS_PATH };
/** Decision 1071: All transactions, the book postings to the account's ledger account beside its feed lines. */
export const bankRegisterPath = (accountId: string): string => `${BANK_ACCOUNTS_PATH}/${encodeURIComponent(accountId)}/register`;
/** 🔒 Close Account and Reopen Account (decision 8840): an account is never deleted, so each is a post that flips `isActive`. */
export const bankAccountClosePath = (accountId: string): string => `${BANK_ACCOUNTS_PATH}/${encodeURIComponent(accountId)}/close`;
export const bankAccountReopenPath = (accountId: string): string => `${BANK_ACCOUNTS_PATH}/${encodeURIComponent(accountId)}/reopen`;
export const PAY_BILLS_PATH = '/banking/pay-bills';
/** 🔒 Decision 10650: the approved expense reports still owed, and one check against one of them. */
export const REIMBURSEMENTS_PATH = '/banking/reimbursements';
export const CHECKS_PATH = '/banking/checks';
/** 🔒 One check's own sheet, served again for a Printed row (decision 0558); the number is on the stock, so the sheet is unchanged. */
export const checkPdfPath = (checkId: string): string => `${CHECKS_PATH}/${encodeURIComponent(checkId)}/pdf`;
/** 🔒 One file, one sheet per ticked check, in list order (decision 0556). Asking for it changes nothing. */
export const checkBatchPdfPath = (checkIds: readonly string[]): string => `${CHECKS_PATH}/batch.pdf?ids=${checkIds.map(encodeURIComponent).join(',')}`;
/** 🔒 The alignment test print: the check template with placeholder data on blank paper, asked for before a live check goes through the printer (piece 27, T3444). Asking for it changes nothing. */
export const CHECK_ALIGNMENT_PDF_PATH = `${CHECKS_PATH}/alignment.pdf`;
/** The batch confirmed: the number on the first sheet, the direction, and which rows printed correctly. */
export const CHECK_CONFIRM_BATCH_PATH = `${CHECKS_PATH}/confirm-batch`;
export const BANK_ALLOCATIONS_PATH = '/banking/allocations';
export const VENDOR_TAX_PATH = '/banking/vendor-tax';
export const FORM_1099_THRESHOLD_PATH = '/banking/settings/form-1099-threshold';
/** Decision 1620 (T858): Settings › Accounting › Card Surcharge, the credit rate read and written; the write is a super admin's. */
export const CARD_SURCHARGE_PATH = '/banking/settings/card-surcharge';
/** Decision 1621 (T858): Settings › Accounting › Documents Inbox Rules, its read, the master address, and one dedicated address or one rule. */
export const DOCUMENTS_INBOX_PATH = '/banking/settings/documents-inbox';
export const documentsInboxAddressPath = (id?: string): string => `${DOCUMENTS_INBOX_PATH}/addresses${id ? `/${encodeURIComponent(id)}` : ''}`;
/** Retiring a dedicated address keeps its row, so it is a post and not a delete (piece 24). */
export const documentsInboxAddressRetirePath = (id: string): string => `${documentsInboxAddressPath(id)}/retire`;
export const documentsInboxRulePath = (id?: string): string => `${DOCUMENTS_INBOX_PATH}/rules${id ? `/${encodeURIComponent(id)}` : ''}`;
export const INFORMATION_RETURNS_PATH = '/banking/information-returns';
export const BANK_TRANSACTIONS_PATH = '/banking/transactions';
export const BANK_RULES_PATH = '/banking/rules';
/** Decision 11000: the reorder's one request. */
export const BANK_RULES_ORDER_PATH = `${BANK_RULES_PATH}/order`;
/** The provider keys the registry has bound, which the connect form offers rather than a free text box (decision 0432). */
export const BANK_FEED_PROVIDERS_PATH = '/banking/feed-providers';
export const RECONCILIATIONS_PATH = '/banking/reconciliations';
/** The Employee Mileage and Expense Report's own routes (piece 24; decision 0199). */
export const EXPENSE_CATEGORIES_PATH = `${EXPENSES_PATH}/categories`;
export const MILEAGE_RATE_PATH = `${EXPENSES_PATH}/mileage-rate`;
/** Decision 8120: the per diem rates Settings lists and adds to, and the one it ends. */
export const PER_DIEM_RATES_PATH = `${EXPENSES_PATH}/per-diem-rates`;
export const perDiemRateEndPath = (id: string): string => `${PER_DIEM_RATES_PATH}/${encodeURIComponent(id)}/end`;
export const EXPENSE_REPORTS_PATH = `${EXPENSES_PATH}/reports`;
export const EXPENSE_LINES_PATH = `${EXPENSES_PATH}/lines`;
/** Decision 1314: Add to Report and Delete on the Expenses tab, over the person's own unreported expenses. */
export const EXPENSE_ADD_TO_REPORT_PATH = `${EXPENSE_LINES_PATH}/add-to-report`;
export const EXPENSE_LINES_DELETE_PATH = `${EXPENSE_LINES_PATH}/delete`;
/** Decision 1315: + Mileage's save, and the reader's own Physical events of the drive's day. */
export const EXPENSE_MILEAGE_PATH = `${EXPENSES_PATH}/mileage`;
/** Decision 2963: the drives the Documents Inbox offers from the person's calendar. */
export const MILEAGE_SUGGESTIONS_PATH = `${EXPENSES_PATH}/mileage-suggestions`;
/** Decision 2963: the miles the map reads for one drive, on its own day or the named day of a series. */
export const EXPENSE_FLAGS_PATH = `${EXPENSES_PATH}/flags`;
export const EXPENSE_UNMATCHED_PATH = `${EXPENSES_PATH}/unmatched`;
/** Decision 2961: the active company cards the Edit Expense window offers under Card, on the Expenses read. */
export const EXPENSE_COMPANY_CARDS_PATH = `${EXPENSES_PATH}/company-cards`;
/** Decision 2961: the Banking page's Company cards section, its list and one card's edit, cancel and reactivate. */
export const COMPANY_CARDS_PATH = '/banking/company-cards';
export const companyCardPath = (id: string, act?: 'cancel' | 'reactivate'): string => `${COMPANY_CARDS_PATH}/${encodeURIComponent(id)}${act ? `/${act}` : ''}`;
/** Decision 1185: the Edit Expense window's one save, one receipt's lines onto a Draft report. */
export const EXPENSE_ENTRIES_PATH = `${EXPENSES_PATH}/entries`;

/** The screens' own routes, the ones the seeded NavItem rows name. */
export const BANKING_ROUTE = '/banking';
export const PAY_BILLS_ROUTE = '/pay-bills';
export const CHECKS_ROUTE = '/checks';
/** One account's own page, reached from the Banking list; the id is appended. */
export const BANK_ACCOUNT_ROUTE = '/banking/account';
/** One account's statement periods; the id is appended (decision 0215). */
export const RECONCILIATION_ROUTE = '/banking/reconcile';
/**
 * 🔒 The expense section is Expenses, and its reports live inside it (piece 24;
 * decisions 0199, 1310): the section at `/expenses`, one report at
 * `/expenses/reports/` and its id.
 */
export const EXPENSES_ROUTE = '/expenses';
export const EXPENSE_REPORTS_ROUTE = '/expenses/reports';
/** 🔨 The section's address before decision 1310, kept only so a link already sent redirects. */
export const OLD_EXPENSE_REPORTS_ROUTE = '/expense-reports';
/** The Documents Inbox, the route the seeded `queue:documents-inbox` NavItem names (piece 12); its receipts half is built (decision 1187). */
export const DOCUMENTS_INBOX_ROUTE = '/documents/inbox';

/**
 * 🔒 The providers that have a bank sign-in of their own (decision 0492).
 * Everything else — the scripted fake above all — is connected by entering
 * the reference the provider issued, because there is no screen to open.
 */
export const SIGN_IN_PROVIDERS: readonly string[] = [PLAID_FEED_KEY, STRIPE_FEED_KEY];

/**
 * The provider's name where a person reads it. The key stays the value the form hands back and the server
 * stores; only the label changes. A key this does not know is shown as it is, so a newly bound provider
 * is never hidden by a missing name.
 */
export function feedProviderName(key: string): string {
  if (key === PLAID_FEED_KEY) return t('settings.integrations.vendor.plaid');
  if (key === STRIPE_FEED_KEY) return t('settings.system.status.vendor.stripeFinancialConnections');
  if (key === SCRIPTED_FEED_KEY) return t('banking.connect.provider.scripted');
  return key;
}
